import { CoreAPI } from '../core-api';
import { createAPIMethod } from '../utils/create-api-method';
import { SDKError } from '../errors/sdk-error';
import {
  logAgentStreamEventError,
  logAgentStreamFinished,
  logAgentStreamHttpError,
  logAgentStreamStart,
} from '../utils/logging';
import { getAPIEndpoint } from '@hero/api';
import { CONFIG } from '@hero/core';
import type {
  AgentSendMessageAPI,
  AgentConfirmMessageAPI,
  AgentGetTranscriptAPI,
  AgentListConversationsAPI,
  AgentMediaUrlAPI,
  AgentSSEEvent,
} from '@hero/api';
import {
  AuthGetMeAPI,
  SendOtpAPI,
  SignOutAPI,
  ChangeUserLangAPI,
  SwitchOrgAPI,
  UserDeleteAccountAPI,
  UserCanUseAppAPI,
  CurrentUserMetaGetAPI,
  CurrentUserMetaUpdateAPI,
  OrderGetOneAPI,
  OrderGetPublicAPI,
  OrderGetWaybillAPI,
  OrderGenerateWaybillAPI,
  OrderWaybillHtmlPreviewAPI,
  OrderWaybillHtmlGenerateAPI,
  OrderUpdateAPI,
  OrderLocationChangePreviewAPI,
  MessageSendAPI,
  CreateMultiOrderAPI,
  VersionCheckAPI,
  LocationSearchAPI,
  AddressAPI,
  locationAutocompleteAPI,
  RegionCreateAPI,
  RegionGetAllCitiesAPI,
  RegionSearchAPI,
  RegionTranslateAPI,
  CurrentUserOrdersAPI,
  MyOrdersAPI,
  MyAnalyticsOverviewAPI,
  MemberGetOneAPI,
  MemberCreateAPI,
  MemberUpdateAPI,
  MemberDeleteAPI,
  MemberGetCurrentAPI,
  MemberCurrentUpdateAPI,
  MemberUsersAPI,
  WalletCurrentUserBalanceAPI,
  InvoiceGetOneAPI,
  InvoiceGetCurrentUserAPI,
  InvoiceOrdersAPI,
  InvoiceEligibleLegsCurrentUserAPI,
  InvoiceCaptureLegsCurrentUserAPI,
  InvoiceOriginalCopyStatusUpdateAPI,
  InvoicePayAPI,
  TransactionGetOneAPI,
  TransactionGetCurrentUserAPI,
  TransactionOrdersAPI,
  AccountGetCurrentUserAPI,
  AccountGetOneCurrentUserAPI,
  AccountGetByPairAPI,
  AccountTransactionGetCurrentUserAPI,
  AccountTransactionGetOneCurrentUserAPI,
  OrgProfileGetAPI,
  OrgProfileUpdateAPI,
  OrgProfileAdminGetAPI,
  OrgProfileAdminUpdateAPI,
  CalculatePriceAPIv2,
  CalculatePriceMultiRoutesAPI,
  CalculateMultiDistancePriceAPI,
  ClientConfigMeAPI,
  ClientGetAllAPI,
  ClientGetOneAPI,
  ClientCreateAPI,
  ClientUpdateAPI,
  ClientDeleteAPI,
  ContactGetAllAPI,
  ContactGetOneAPI,
  ContactCreateAPI,
  ContactUpdateAPI,
  ContactDeleteAPI,
  LeadPublicCreateAPI,
  ReferralLinkResolveAPI,
  ReferralTrackingCreateAPI,
  AnalyticsLeadsAPI,
  AnalyticsUsersAPI,
  UserGetManyAPI,
  UserUpdateAPI,
  SavedLocationGetAllAPI,
  SavedLocationGetOneAPI,
  SavedLocationSearchAPI,
  SavedLocationCreateAPI,
  SavedLocationUpdateAPI,
  SavedLocationDeleteAPI,
  SavedLocationResolveGoogleUrlAPI,
  RouteGetOneAPI,
  RouteGetAllAPI,
  RouteCreateAPI,
  RouteUpdateAPI,
  RouteDeleteAPI,
  AgreementGetOneAPI,
  AgreementGetManyAPI,
  AgreementCreateAPI,
  AgreementUpdateAPI,
  FinancingAcceptOfferAPI,
  FinancingAdminGetAllAPI,
  FinancingAdminGetOneAPI,
  FinancingAdminUpdateInvoiceAPI,
  FinancingAdminUpsertOfferAPI,
  FinancingApproveRepaymentAPI,
  FinancingCancelAPI,
  FinancingCreateApplicationAPI,
  FinancingGetApplicationsAPI,
  FinancingGetOfferLenderContactsAPI,
  FinancingGetPartnersAPI,
  FinancingLenderExpressInterestAPI,
  FinancingLenderMarkFundedAPI,
  FinancingLenderMarkRepaidAPI,
  FinancingLenderRejectRepaymentAPI,
  FinancingLenderSubmitOfferAPI,
  FinancingLenderViewAPI,
  FinancingMarkOutcomeAPI,
  FinancingPartnerExpressInterestAPI,
  FinancingPartnerGetAPI,
  FinancingPartnerSubmitOfferAPI,
  FinancingRejectRepaymentAPI,
  FinancingSendToLendersAPI,
  FinancingSubmitRepaymentReceiptAPI,
  FinancingUpdateTagsAPI,
  FinancingUploadInvoiceDocumentAPI,
  LenderGetActiveAPI,
  OrderEscalateAPI,
  OrderContractCounterAPI,
  OrderContractCancelAPI,
  OrderContractCompleteAPI,
  OfferCreateAPI,
  OfferPreviewBroadcastAPI,
  OfferGetInboxAPI,
  OfferGetOutboxAPI,
  OfferGetOneAPI,
  OfferGetOrderPreviewAPI,
  OfferAcceptTargetAPI,
  OfferCounterTargetAPI,
  OfferDeclineTargetAPI,
  OfferWithdrawTargetAPI,
  OfferWithdrawAPI,
  OfferLoadBoardSearchAPI,
  ConnectionGetAllAPI,
  ConnectionGetOneAPI,
  ConnectionAcceptAPI,
  ConnectionBlockAPI,
  ProfileGetAllAPI,
  ProfileGetOneAPI,
  ProfileCreateAdminAPI,
  ProfileUpdateAdminAPI,
  ProfileDeleteAdminAPI,
  ProfileBulkImportAdminAPI,
  ProfileSuggestAPI,
  OrgGetAllAPI,
  OrgGetOneAPI,
  SignInAPI,
  SignUpAPI,
  AdminCreateOrgAPI,
  EntityDirectorySearchAPI,
  ClientLookupByLinkAPI,
  // sales (subscriptions + Hero-side billing)
  SubscriptionGetMineAPI,
  SubscriptionGetUsageAPI,
  SubscriptionGetAdminAPI,
  SubscriptionAdminGetAllAPI,
  SubscriptionSubscribeAPI,
  SubscriptionPayAPI,
  SubscriptionChangePlanAPI,
  SubscriptionChangeIntervalAPI,
  SubscriptionChangeSeatsAPI,
  SubscriptionAddAddonAPI,
  SubscriptionRemoveAddonAPI,
  SubscriptionCancelAPI,
  SubscriptionAdminOverrideAPI,
  SubscriptionAdminForceRenewAPI,
  SubscriptionAdminGrantCreditsAPI,
  SubscriptionAdminRevokeCreditsAPI,
  SubscriptionAdminResetFreeCreditsAPI,
  SubscriptionPaymentMethodCheckoutAPI,
  SubscriptionPaymentMethodDeleteAPI,
  BillingGetOneAPI,
  BillingGetManyAPI,
  BillingAdminGetAllAPI,
  BillingAdminCancelAPI,
  BillingTransactionGetOneAPI,
  BillingTransactionGetManyAPI,
  BillingTransactionAdminRefundAPI,
  BillingTransactionByGatewayPaymentIdAPI,
  FeaturesGetMineAPI,
  FeaturesGetAdminAPI,
} from '@hero/api';

/**
 * Common order APIs that should be included in all SDKs' orders namespace
 * These are order-related APIs that are available across all roles
 */
export function createCommonOrderMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get order by ID
     *
     * API: {@link OrderGetOneAPI}
     */
    getById: createAPIMethod<OrderGetOneAPI>(coreAPI, 'order.getOne'),

    /**
     * Public order by shareable ref or id (no auth). Returns a lean
     * DTO — route + status only, never prices or PII.
     *
     * API: {@link OrderGetPublicAPI}
     */
    getPublic: createAPIMethod<OrderGetPublicAPI>(coreAPI, 'order.getPublic'),

    /**
     * Get order waybill
     *
     * API: {@link OrderGetWaybillAPI}
     */
    getWaybill: createAPIMethod<OrderGetWaybillAPI>(coreAPI, 'order.getWaybill'),
    /**
     * Request (async) waybill generation for an order.
     * Marks `waybill.status = generating` and returns immediately.
     *
     * API: {@link OrderGenerateWaybillAPI}
     */
    generateWaybill: createAPIMethod<OrderGenerateWaybillAPI>(coreAPI, 'order.generateWaybill'),
    getWaybillHtmlPreview: createAPIMethod<OrderWaybillHtmlPreviewAPI>(
      coreAPI,
      'order.getWaybillHtmlPreview',
    ),
    generateWaybillHtml: createAPIMethod<OrderWaybillHtmlGenerateAPI>(
      coreAPI,
      'order.generateWaybillHtml',
    ),
    /**
     * Get current user orders. This applies to all roles (drivers, business users, carrier users).
     *
     * API: {@link CurrentUserOrdersAPI}
     */
    getCurrentUserOrders: createAPIMethod<CurrentUserOrdersAPI>(coreAPI, 'order.currentUserOrders'),

    /**
     * Tenant-aware unified "my orders" lookup.
     *
     * Auto-scopes via `Order.tenants[]` so the same SDK method works
     * for every role (carrier company / business customer / individual
     * customer / driver / broker). Optional `clientId` AND-narrows to
     * "my orders involving this CRM Client" — used by the workspace
     * orders tab and the client drawer's orders tab.
     *
     * Prefer this over `getCurrentUserOrders` for new code: it
     * exposes the full filter surface from `OrdersQueryAPI` and
     * supports CRM-client narrowing.
     *
     * API: {@link MyOrdersAPI}
     */
    getMyOrders: createAPIMethod<MyOrdersAPI>(coreAPI, 'order.myOrders'),

    /**
     * Update order
     *
     * API: {@link OrderUpdateAPI}
     */
    updateOrder: createAPIMethod<OrderUpdateAPI>(coreAPI, 'order.updateOne'),

    /**
     * Dry-run a destination edit: whether it crosses a city boundary,
     * whether that's allowed on this order, and what the new lane quotes.
     *
     * Call this before sending the destinations PATCH so the user sees the
     * price consequence before it happens.
     *
     * API: {@link OrderLocationChangePreviewAPI}
     */
    previewOrderLocationChange: createAPIMethod<OrderLocationChangePreviewAPI>(
      coreAPI,
      'order.locationChangePreview',
    ),

    /**
     * Create multiple orders
     *
     * API: {@link CreateMultiOrderAPI}
     */
    createMultiOrder: createAPIMethod<CreateMultiOrderAPI>(coreAPI, 'order.createMulti'),

    /**
     * Manually escalate the allocation cascade for the caller's order. When
     * `promoteToPublic=true` and the caller is `Order.shipper`, broadcasts to
     * the public load board; otherwise forces `private_network`.
     *
     * API: {@link OrderEscalateAPI}
     */
    escalate: createAPIMethod<OrderEscalateAPI>(coreAPI, 'order.escalate'),

    /**
     * Force a counter-state on an already-pushed contract leg.
     *
     * API: {@link OrderContractCounterAPI}
     */
    counterContract: createAPIMethod<OrderContractCounterAPI>(
      coreAPI,
      'order.contractCounter',
    ),

    /**
     * Cancel an already-pushed contract leg.
     *
     * API: {@link OrderContractCancelAPI}
     */
    cancelContract: createAPIMethod<OrderContractCancelAPI>(coreAPI, 'order.contractCancel'),

    /**
     * Mark a contract leg as completed.
     *
     * API: {@link OrderContractCompleteAPI}
     */
    completeContract: createAPIMethod<OrderContractCompleteAPI>(
      coreAPI,
      'order.contractComplete',
    ),
  };
}

/**
 * Common analytics APIs. The new `getMyAnalyticsOverview` is the
 * role-neutral, lens-aware twin of {@link createCommonOrderMethods}'s
 * `getMyOrders`. It sits next to it so every SDK (carrier, customer,
 * admin) can compose it the same way and avoid pinning the dashboard
 * home / mobile admin home to a role-locked URL.
 */
export function createCommonAnalyticsMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Canonical, role-neutral analytics overview. Powers the home /
     * dashboard "3-card" tiles on every consumer of Hero
     * (carrier-dashboard, customer-dashboard, carrier-app home, and
     * the AI order agent's `get_order_analytics_summary` tool) via a
     * single unified backend service. Every count on the response
     * comes from the shared `OrderQueryService.transformQueryAPI`
     * engine, so numbers here match `getMyOrders` and the AI count
     * tools for identical params.
     *
     * Accepts an optional `lens` to narrow the DB query to one side
     * of a contract leg (`as_carrier` | `as_customer` | `as_shipper`
     * | `as_driver` | `none`). Omit for the tenant-wide view (every
     * order the caller is a tenant of), regardless of contract side —
     * this is what the mobile home and web dashboard have always shipped.
     *
     * Returns the unified `MyAnalyticsOverviewData` shape. Seller-only
     * blocks (`earnings`, `needsDriver`, `utilization`, `drivers`) are
     * populated with zero values for
     * buyer-lens callers so the shape stays uniform; consumer UIs
     * gate on `displayForUser` to hide them where irrelevant.
     *
     * API: {@link MyAnalyticsOverviewAPI}
     */
    getMyAnalyticsOverview: createAPIMethod<MyAnalyticsOverviewAPI>(
      coreAPI,
      'analytics.myAnalyticsOverview',
    ),

    /**
     * Admin-only — lead CRM funnel + source / assignee rollups.
     * Returns 401/403 for non-super-admin callers.
     *
     * API: {@link AnalyticsLeadsAPI}
     */
    getLeads: createAPIMethod<AnalyticsLeadsAPI>(coreAPI, 'analytics.getLeads'),

    /**
     * Admin-only — network Users rollup (`total`, `signups`,
     * `byStatus`, `byRole`, `byClass`, `activeInLast7Days`). Powers
     * the admin Users page analytics tile.
     *
     * API: {@link AnalyticsUsersAPI}
     */
    getUsers: createAPIMethod<AnalyticsUsersAPI>(coreAPI, 'analytics.getUsers'),
  };
}

/**
 * Common User APIs (super-admin surface). The `user` namespace on
 * the admin SDK reuses these methods; the customer / carrier SDKs
 * currently don't expose them (only Members go there — the User
 * collection is admin-facing).
 */
export function createCommonUserMethods(coreAPI: CoreAPI) {
  return {
    /**
     * List users by flat filter set (`userGetManyAPISchema`). Super
     * admin only.
     *
     * API: {@link UserGetManyAPI}
     */
    getMany: createAPIMethod<UserGetManyAPI>(coreAPI, 'user.getMany'),

    /**
     * Patch a User document. Auth-v2: `status` is settable ONLY by
     * super-admins (backend enforces via `AuthGuard` + service-layer
     * check). When flipping to `blocked`, every active
     * `RefreshSession` for the user is revoked.
     *
     * API: {@link UserUpdateAPI}
     */
    updateOne: createAPIMethod<UserUpdateAPI>(coreAPI, 'user.updateOne'),
  };
}

/**
 * Common offer APIs (marketplace + private-network broadcasts). Shared across
 * carrier / customer / admin SDKs because every role can act as a buyer or a
 * target depending on context.
 */
export function createCommonOfferMethods(coreAPI: CoreAPI) {
  return {
    /** Create one Offer. */
    create: createAPIMethod<OfferCreateAPI>(coreAPI, 'offer.create'),

    /** Preview pricing rows for a list of candidate parties before publish. */
    previewBroadcast: createAPIMethod<OfferPreviewBroadcastAPI>(
      coreAPI,
      'offer.previewBroadcast',
    ),

    /** Offers where the caller is a target (their inbox). */
    inbox: createAPIMethod<OfferGetInboxAPI>(coreAPI, 'offer.inbox'),

    /** Offers proposed by the caller (their outbox). */
    outbox: createAPIMethod<OfferGetOutboxAPI>(coreAPI, 'offer.outbox'),

    /** Read one Offer (server projects per viewer). */
    getOne: createAPIMethod<OfferGetOneAPI>(coreAPI, 'offer.getOne'),

    /**
     * Privileged Order preview for an offer target — exposes the underlying
     * Order without requiring the caller to be in `Order.tenants[]`.
     */
    orderPreview: createAPIMethod<OfferGetOrderPreviewAPI>(coreAPI, 'offer.orderPreview'),

    /** Accept one target row → atomic Offer+Order write. */
    acceptTarget: createAPIMethod<OfferAcceptTargetAPI>(coreAPI, 'offer.acceptTarget'),

    /** Counter-propose on one target row (Pattern B). */
    counterTarget: createAPIMethod<OfferCounterTargetAPI>(coreAPI, 'offer.counterTarget'),

    /** Decline one target row. */
    declineTarget: createAPIMethod<OfferDeclineTargetAPI>(coreAPI, 'offer.declineTarget'),

    /** Buyer withdraws one target row. */
    withdrawTarget: createAPIMethod<OfferWithdrawTargetAPI>(
      coreAPI,
      'offer.withdrawTarget',
    ),

    /** Buyer withdraws the whole Offer. */
    withdraw: createAPIMethod<OfferWithdrawAPI>(coreAPI, 'offer.withdraw'),

    /** Public load board feed. */
    loadBoard: createAPIMethod<OfferLoadBoardSearchAPI>(coreAPI, 'offer.loadBoard'),
  };
}

/**
 * Common member APIs that should be included in all SDKs' members namespace
 * These are member-related APIs that are available across all roles
 */
export function createCommonMemberMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get all members (users)
     *
     * API: {@link MemberUsersAPI}
     */
    getUsers: createAPIMethod<MemberUsersAPI>(coreAPI, 'member.getUsers'),

    /**
     * Get a single member by ID
     *
     * API: {@link MemberGetOneAPI}
     */
    getOneMember: createAPIMethod<MemberGetOneAPI>(coreAPI, 'member.getOne'),

    /**
     * Create member
     *
     * API: {@link MemberCreateAPI}
     */
    createMember: createAPIMethod<MemberCreateAPI>(coreAPI, 'member.createOne'),

    /**
     * Update member
     *
     * API: {@link MemberUpdateAPI}
     */
    updateMember: createAPIMethod<MemberUpdateAPI>(coreAPI, 'member.updateOne'),

    /**
     * Delete member
     *
     * API: {@link MemberDeleteAPI}
     */
    deleteMember: createAPIMethod<MemberDeleteAPI>(coreAPI, 'member.deleteOne'),

    /**
     * Get current member
     *
     * API: {@link MemberGetCurrentAPI}
     */
    getCurrentMember: createAPIMethod<MemberGetCurrentAPI>(coreAPI, 'member.getCurrentMember'),

    /**
     * Update current member
     *
     * API: {@link MemberCurrentUpdateAPI}
     */
    updateCurrentMember: createAPIMethod<MemberCurrentUpdateAPI>(
      coreAPI,
      'member.updateCurrentMember',
    ),
  };
}

/**
 * Common location APIs that should be included in all SDKs' location namespace
 * These are location-related APIs that are available across all roles
 */
export function createCommonLocationMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Search locations
     *
     * API: {@link LocationSearchAPI}
     */
    search: createAPIMethod<LocationSearchAPI>(coreAPI, 'location.search'),

    /**
     * Autocomplete locations (Google Places + saved locations)
     *
     * API: {@link locationAutocompleteAPI}
     */
    autocomplete: createAPIMethod<locationAutocompleteAPI>(coreAPI, 'location.autocomplete'),

    /**
     * Get address from lat/lng
     *
     * API: {@link AddressAPI}
     */
    address: createAPIMethod<AddressAPI>(coreAPI, 'location.address'),
  };
}

/**
 * Common APIs that are available across all SDKs (customer, carrier, admin)
 * These APIs are truly common and don't belong to any specific domain
 */
export function createCommonAPI(coreAPI: CoreAPI) {
  return {
    /**
     * Version - Common
     */
    version: {
      /**
       * Check app version
       * Schema: versionCheckAPISchema
       */
      check: createAPIMethod<VersionCheckAPI>(coreAPI, 'version.checkVersion'),
    },

    /**
     * Location - Common
     */
    location: createCommonLocationMethods(coreAPI),

    /**
     * Region - Common
     *
     * `search` and `translate` are the two read-paths every dashboard / mobile app
     * shares (AI / NLU resolution + render-time language resolution). See
     * `docs/regions/01-translation-and-cache.md` and `03-search-and-ai.md`.
     */
    region: {
      /**
       * Get all cities
       * No schema (no validation in controller)
       */
      getAllCities: createAPIMethod<RegionGetAllCitiesAPI>(coreAPI, 'region.getAllCities'),

      /**
       * Create (or resolve) a region from a lat/lng pin. Idempotent — a point
       * inside an existing region's boundaries returns that region. Any
       * authenticated user may call it; non-super-admin creations are forced
       * to `status: 'needs_review'` server-side (Super-Admin review queue).
       *
       * Used by the region picker's "Pin from map" escalation so a missing
       * city can be added to the network on the spot.
       *
       * API: {@link RegionCreateAPI}
       */
      create: createAPIMethod<RegionCreateAPI>(coreAPI, 'region.createRegion'),

      /**
       * Free-text search across regions in any language (AI / NLU resolution).
       *
       * API: {@link RegionSearchAPI}
       */
      search: createAPIMethod<RegionSearchAPI>(coreAPI, 'region.search'),

      /**
       * Resolve a batch of `customCode`s into display names in the viewer's
       * language. Public, batched up to 100, edge-cached with 24h ETag.
       *
       * API: {@link RegionTranslateAPI}
       */
      translate: createAPIMethod<RegionTranslateAPI>(coreAPI, 'region.translate'),
    },
  };
}

/**
 * Common auth APIs that should be included in all SDKs' auth namespace.
 * These are auth-related APIs that are available across all roles.
 */
export function createCommonAuthMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get the current user's identity (user + org + driver) based on the JWT.
     *
     * API: {@link AuthGetMeAPI}
     */
    me: createAPIMethod<AuthGetMeAPI>(coreAPI, 'auth.me'),

    /**
     * Send OTP
     *
     * API: {@link SendOtpAPI}
     */
    sendOTP: createAPIMethod<SendOtpAPI>(coreAPI, 'auth.sendOTP'),

    /**
     * Unified sign-in (relations-v2). One endpoint for every user
     * type (customer, carrier, driver, broker, admin). Replaces the
     * deprecated `signInCustomer` / `signInCarrier` aliases. See
     * `docs/relations-v2/08-user-identity.md`.
     *
     * API: {@link SignInAPI}
     */
    signIn: createAPIMethod<SignInAPI>(coreAPI, 'auth.signIn'),

    /**
     * Unified sign-up (relations-v2). Discriminated by the wizard's
     * `personType` (`'individual_customer' | 'company' | 'driver'`).
     * Backend creates `Org` as the primary collection and reverse-
     * mirrors to legacy `Business` / `Carrier`. See
     * `docs/relations-v2/08-user-identity.md`.
     *
     * API: {@link SignUpAPI}
     */
    signUp: createAPIMethod<SignUpAPI>(coreAPI, 'auth.signUp'),

    /**
     * Sign out
     *
     * API: {@link SignOutAPI}
     */
    logout: createAPIMethod<SignOutAPI>(coreAPI, 'auth.signOut'),

    /**
     * Change language
     *
     * API: {@link ChangeUserLangAPI}
     */
    changeLanguage: createAPIMethod<ChangeUserLangAPI>(coreAPI, 'auth.changeLang'),

    /**
     * Switch organization
     *
     * API: {@link SwitchOrgAPI}
     */
    switchOrg: createAPIMethod<SwitchOrgAPI>(coreAPI, 'auth.switchOrg'),

    /**
     * Delete account
     *
     * API: {@link UserDeleteAccountAPI}
     */
    deleteAccount: createAPIMethod<UserDeleteAccountAPI>(coreAPI, 'auth.deleteAccount'),

    /**
     * Check if user can use app
     *
     * API: {@link UserCanUseAppAPI}
     */
    canUseApp: createAPIMethod<UserCanUseAppAPI>(coreAPI, 'auth.canUseApp'),
  };
}

/**
 * `current-user/*` — endpoints that always operate on the caller's
 * own row. Ships with every SDK (customer, carrier, admin) because
 * the agent-facing meta writer (`updateMeta`) is used by every
 * network-party dashboard and both mobile apps.
 *
 * See `docs/ai/user-signals.md` for the payload shape.
 */
export function createCommonCurrentUserMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Read the caller's `User` document with `meta` populated.
     *
     * API: {@link CurrentUserMetaGetAPI}
     */
    getMeta: createAPIMethod<CurrentUserMetaGetAPI>(coreAPI, 'currentUser.getMeta'),
    /**
     * Partial write into the caller's `User.meta`. Every section is
     * orthogonal — send only the pieces that changed (presence
     * heartbeat, permissions snapshot, timezone, user-stated
     * location, channel policy).
     *
     * API: {@link CurrentUserMetaUpdateAPI}
     */
    updateMeta: createAPIMethod<CurrentUserMetaUpdateAPI>(coreAPI, 'currentUser.updateMeta'),
  };
}

/**
 * Super-admin auth methods (relations-v2). Lives next to
 * {@link createCommonAuthMethods} so admin SDKs can compose it
 * without duplicating wiring. The unified `adminCreateOrg` replaces
 * the deprecated `adminCreateCustomer` / `adminCreateCarrier`.
 */
export function createAdminAuthMethods(coreAPI: CoreAPI) {
  return {
    adminCreateOrg: createAPIMethod<AdminCreateOrgAPI>(coreAPI, 'auth.adminCreateOrg'),
  };
}

/**
 * Common payment APIs that should be included in all SDKs' payment namespace
 * These are payment-related APIs that are available across all roles
 */
export function createCommonPaymentMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get current user balance
     *
     * API: {@link WalletCurrentUserBalanceAPI}
     */
    getCurrentUserBalance: createAPIMethod<WalletCurrentUserBalanceAPI>(
      coreAPI,
      'payment.getUserBalance',
    ),

    /**
     * Get invoice by ID
     *
     * API: {@link InvoiceGetOneAPI}
     */
    getInvoice: createAPIMethod<InvoiceGetOneAPI>(coreAPI, 'payment.getOneInvoice'),

    /**
     * Get current user invoices
     *
     * API: {@link InvoiceGetCurrentUserAPI}
     */
    getCurrentUserInvoices: createAPIMethod<InvoiceGetCurrentUserAPI>(
      coreAPI,
      'payment.getUserInvoices',
    ),

    /**
     * Get invoice orders
     *
     * API: {@link InvoiceOrdersAPI}
     */
    getInvoiceOrders: createAPIMethod<InvoiceOrdersAPI>(coreAPI, 'payment.getInvoiceOrders'),

    /**
     * Get transaction by ID
     *
     * API: {@link TransactionGetOneAPI}
     */
    getTransaction: createAPIMethod<TransactionGetOneAPI>(coreAPI, 'payment.getOneTransaction'),

    /**
     * Get current user transactions with category/status/invoiceId filters
     *
     * API: {@link TransactionGetCurrentUserAPI}
     */
    getCurrentUserTransactions: createAPIMethod<TransactionGetCurrentUserAPI>(
      coreAPI,
      'payment.getUserTransactions',
    ),

    /**
     * Get orders linked to a transaction
     *
     * API: {@link TransactionOrdersAPI}
     */
    getTransactionOrders: createAPIMethod<TransactionOrdersAPI>(
      coreAPI,
      'payment.getTransactionOrders',
    ),

    // ───────────────────── bilateral Account ─────────────────────
    // The party-facing Account / AccountTransaction surface.
    // Mirrors the admin-only `payment.adminGetAllAccounts` etc. The
    // controller scopes every call by the viewer's org via
    // `CurrentUserService` so a single shared SDK method is safe to
    // mount on every dashboard (carrier / customer / driver).
    //
    // See `docs/relations-v2/10-account-and-account-transactions.md`
    // for the bilateral model background.

    /**
     * List the viewer's bilateral `Account` documents.
     *
     * API: {@link AccountGetCurrentUserAPI}
     */
    getAccounts: createAPIMethod<AccountGetCurrentUserAPI>(
      coreAPI,
      'payment.getCurrentUserAccounts',
    ),

    /**
     * Get a single bilateral `Account` by id. 403 if the viewer is
     * not on `account.parties[]`.
     *
     * API: {@link AccountGetOneCurrentUserAPI}
     */
    getAccount: createAPIMethod<AccountGetOneCurrentUserAPI>(
      coreAPI,
      'payment.getCurrentUserAccount',
    ),

    /**
     * Exact-pair lookup. Returns the bilateral Account for an
     * unordered pair + currency, or `null` if none exists yet. The
     * caller must be one of the two parties.
     *
     * API: {@link AccountGetByPairAPI}
     */
    getAccountByPair: createAPIMethod<AccountGetByPairAPI>(
      coreAPI,
      'payment.getCurrentUserAccountByPair',
    ),

    /**
     * List the viewer's `AccountTransaction` rows. Supports
     * filtering by `accountId | invoiceId | partyId | type`.
     *
     * API: {@link AccountTransactionGetCurrentUserAPI}
     */
    getAccountTransactions: createAPIMethod<AccountTransactionGetCurrentUserAPI>(
      coreAPI,
      'payment.getCurrentUserAccountTransactions',
    ),

    /**
     * Get a single `AccountTransaction` by id (visible only when
     * the viewer is on the associated account).
     *
     * API: {@link AccountTransactionGetOneCurrentUserAPI}
     */
    getAccountTransaction: createAPIMethod<AccountTransactionGetOneCurrentUserAPI>(
      coreAPI,
      'payment.getCurrentUserAccountTransaction',
    ),

    // ───────────────── manual invoice creation ─────────────────
    // Party-facing twin of the admin "captureLegs" flow. The
    // backend pins the viewer as the seller on every leg, so
    // exposing the method on the common SDK is safe — there is no
    // way for a buyer to use it to create an invoice for another
    // org.

    /**
     * List delivered legs the viewer can turn into invoices. The
     * viewer is implicitly on the seller side; `clientId` narrows
     * the buyer side to a single CRM Client.
     *
     * API: {@link InvoiceEligibleLegsCurrentUserAPI}
     */
    getEligibleLegs: createAPIMethod<InvoiceEligibleLegsCurrentUserAPI>(
      coreAPI,
      'payment.getEligibleLegs',
    ),

    /**
     * Bundle the selected legs into ONE invoice (one item per
     * leg) and create it. `expectedDueDate` is required and lands
     * on `Invoice.period.due`; optional `invoiceNumber` seeds a
     * shell `invoiceProof` carrying just the ref; optional
     * `originalCopyStatus` overrides the POD-mirror status that
     * would otherwise be derived from the legs' orders.
     *
     * API: {@link InvoiceCaptureLegsCurrentUserAPI}
     */
    captureLegs: createAPIMethod<InvoiceCaptureLegsCurrentUserAPI>(
      coreAPI,
      'payment.captureLegs',
    ),

    /**
     * Mutate the original-invoice-copy lifecycle status on a
     * single invoice (e.g. seller marks `sent`, buyer marks
     * `received`). Same audit/event semantics as the POD status
     * updater on Order. Backend enforces who can write which
     * transitions based on role.
     *
     * API: {@link InvoiceOriginalCopyStatusUpdateAPI}
     */
    updateInvoiceOriginalCopyStatus:
      createAPIMethod<InvoiceOriginalCopyStatusUpdateAPI>(
        coreAPI,
        'payment.updateInvoiceOriginalCopyStatus',
      ),

    /**
     * Record an off-platform payment against a single invoice.
     * Role-neutral: any party on `Invoice.partyIds` may call it;
     * the backend validates the caller is on the invoice and caps
     * `amount` to `Invoice.amount.remaining`.
     *
     * Creates an `AccountTransaction` with `images[]` set from the
     * optional `proofUrl`, stamps `Invoice.paidAt` when the
     * payment closes the invoice, and uses the optional `paidAt`
     * for both date fields so back-dated entries stay consistent.
     *
     * API: {@link InvoicePayAPI}
     */
    payInvoice: createAPIMethod<InvoicePayAPI>(coreAPI, 'payment.payInvoice'),
  };
}

/**
 * `orgProfile` namespace — section-keyed, field-level partial profile
 * reads and writes. Shared across every consumer SDK because the
 * controller scopes by JWT.
 *
 * The wire body is multi-section (`{ displayName?, logo?, legal?,
 * bank?, settings? }`) and field-level partial — sending
 * `{ bank: { IBAN: 'SA…' } }` updates ONLY `Bank.IBAN`. See
 * `OrgProfileService` for the dotted-path merge guarantees.
 */
export function createCommonOrgProfileMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Read the current user's org profile (flat
     * `OrganizationProfile` projection from `Org + Bank`).
     *
     * API: {@link OrgProfileGetAPI}
     */
    get: createAPIMethod<OrgProfileGetAPI>(coreAPI, 'orgProfile.get'),

    /**
     * Section-keyed partial update on the current user's org. Any
     * section omitted from the body is left untouched server-side;
     * any FIELD omitted from a present section is also left
     * untouched. Empty strings on identity fields are rejected at
     * the schema layer.
     *
     * API: {@link OrgProfileUpdateAPI}
     */
    update: createAPIMethod<OrgProfileUpdateAPI>(coreAPI, 'orgProfile.update'),
  };
}

/**
 * Admin variants of {@link createCommonOrgProfileMethods}. Compose
 * this on the admin SDK only — backend gates both routes behind
 * `@SuperAdmin()`.
 */
export function createAdminOrgProfileMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Admin: read any org's profile by id.
     *
     * API: {@link OrgProfileAdminGetAPI}
     */
    adminGet: createAPIMethod<OrgProfileAdminGetAPI>(coreAPI, 'orgProfile.adminGet'),

    /**
     * Admin: section-keyed partial update on any org by id. Same
     * partial-merge semantics as the user variant.
     *
     * API: {@link OrgProfileAdminUpdateAPI}
     */
    adminUpdate: createAPIMethod<OrgProfileAdminUpdateAPI>(
      coreAPI,
      'orgProfile.adminUpdate',
    ),
  };
}

/**
 * Common client config APIs that should be included in all SDKs' clientConfig namespace.
 * These are config-related APIs that are available across all roles.
 */
export function createCommonClientConfigMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get the current organization's client config
     *
     * API: {@link ClientConfigMeAPI}
     */
    me: createAPIMethod<ClientConfigMeAPI>(coreAPI, 'clientConfig.me'),
  };
}

/**
 * Common price APIs that should be included in all SDKs' price namespace.
 * These are pricing-related APIs that are available across all roles.
 */
export function createCommonPriceMethods(coreAPI: CoreAPI) {
  return {
    /**
     * @deprecated Use calcSingleRoutePrice instead
     *
     * API: {@link CalculateMultiDistancePriceAPI}
     */
    calculateMultiPrice: createAPIMethod<CalculateMultiDistancePriceAPI>(
      coreAPI,
      'price.calculateMultiPrice',
    ),

    /**
     * Calculate price v2. Single route price. from and to.
     *
     * API: {@link CalculatePriceAPIv2}
     */
    calcSingleRoutePrice: createAPIMethod<CalculatePriceAPIv2>(coreAPI, 'price.calculatePriceV2'),

    /**
     * Calculate price for multiple routes such as [Riyadh, Jeddah, Mecca].
     *
     * Always the first location is the origin/pickup destination, and the rest are drop off destinations.
     *
     * API: {@link CalculatePriceMultiRoutesAPI}
     */
    calcMultiRoutesPrice: createAPIMethod<CalculatePriceMultiRoutesAPI>(
      coreAPI,
      'price.calculatePriceMultiRoutes',
    ),
  };
}

/**
 * Common client APIs that should be included in all SDKs' `clients`
 * namespace. The Client CRM is shared between customer-role, carrier-role,
 * and admin contexts (each filters by auth scope).
 *
 * Replaces the legacy `createCommonCustomerMethods`.
 */
export function createCommonClientMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get all clients
     *
     * API: {@link ClientGetAllAPI}
     */
    getClients: createAPIMethod<ClientGetAllAPI>(coreAPI, 'clients.getAll'),

    /**
     * Get client by ID
     *
     * API: {@link ClientGetOneAPI}
     */
    getClientById: createAPIMethod<ClientGetOneAPI>(coreAPI, 'clients.getOne'),

    /**
     * Create client
     *
     * API: {@link ClientCreateAPI}
     */
    createClient: createAPIMethod<ClientCreateAPI>(coreAPI, 'clients.createOne'),

    /**
     * Update client
     *
     * API: {@link ClientUpdateAPI}
     */
    updateClient: createAPIMethod<ClientUpdateAPI>(coreAPI, 'clients.updateOne'),

    /**
     * Delete client
     *
     * API: {@link ClientDeleteAPI}
     */
    deleteClient: createAPIMethod<ClientDeleteAPI>(coreAPI, 'clients.deleteOne'),

    /**
     * Find a Client in the caller's own Org that already links to a
     * given Profile / Org id. Used by the unified Client-create
     * picker to gate duplicate creation (returns `null` when no
     * match exists, the existing Client otherwise).
     *
     * API: {@link ClientLookupByLinkAPI}
     */
    lookupByLink: createAPIMethod<ClientLookupByLinkAPI>(coreAPI, 'clients.lookupByLink'),
  };
}

/**
 * Common entity-directory APIs (relations-v2). Unified search across
 * the global brand directory (`Profile`) and signed-up Hero `Org`s,
 * dedup'd into a single result list. Powers the Client-create picker
 * across every role-specific SDK.
 */
export function createCommonEntityDirectoryMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Unified Profile + Org search; dedup'd via `Profile.linkedOrgId`.
     *
     * API: {@link EntityDirectorySearchAPI}
     */
    search: createAPIMethod<EntityDirectorySearchAPI>(
      coreAPI,
      'entityDirectory.search',
    ),
  };
}

/**
 * Common contact APIs that should be included in all SDKs' contacts namespace.
 * Contact CRM is shared between customer, carrier, and admin roles (each filters by auth context).
 */
export function createCommonContactMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get all contacts
     *
     * API: {@link ContactGetAllAPI}
     */
    getContacts: createAPIMethod<ContactGetAllAPI>(coreAPI, 'contacts.getAll'),

    /**
     * Get contact by ID
     *
     * API: {@link ContactGetOneAPI}
     */
    getContactById: createAPIMethod<ContactGetOneAPI>(coreAPI, 'contacts.getOne'),

    /**
     * Create contact
     *
     * API: {@link ContactCreateAPI}
     */
    createContact: createAPIMethod<ContactCreateAPI>(coreAPI, 'contacts.createOne'),

    /**
     * Update contact
     *
     * API: {@link ContactUpdateAPI}
     */
    updateContact: createAPIMethod<ContactUpdateAPI>(coreAPI, 'contacts.updateOne'),

    /**
     * Delete contact
     *
     * API: {@link ContactDeleteAPI}
     */
    deleteContact: createAPIMethod<ContactDeleteAPI>(coreAPI, 'contacts.deleteOne'),
  };
}

/**
 * Public lead-intake methods. Exposed on every SDK (carrier, customer,
 * admin) so the landing pages / marketing sites can submit a demo /
 * "talk to sales" request through the core SDK without authentication.
 *
 * The backend endpoint is `@Public()` and rate-limited by a per-IP
 * token bucket. Admin-only lead CRUD lives in the admin SDK's
 * `createLeadsAPI`.
 */
export function createCommonLeadPublicMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Submit a public landing-page lead. Flat shape:
     * `{ personName?, phone?, email?, companyName?, interest?,
     * language?, sourceMeta? }`. The backend stamps
     * `meta.source = 'landing_demo_form'` plus IP / userAgent.
     *
     * Schema: `leadPublicCreateAPISchema`. Returns the new lead's
     * `_id`.
     *
     * API: {@link LeadPublicCreateAPI}
     */
    submit: createAPIMethod<LeadPublicCreateAPI>(coreAPI, 'leads.publicCreate'),
  };
}

/**
 * Public referral-program methods. Exposed on every SDK so the landing
 * pages and the dashboard can record a `?r=` visit and resolve a code
 * without authentication. Admin CRUD (partners, links, tracking,
 * rewards) lives in the admin SDK's `createReferralAdminAPI`.
 *
 * Both endpoints are `@Public()`; `track` is rate-limited per IP.
 * See `docs/referral.md`.
 */
export function createCommonReferralMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Resolve a referral code to its public shape (title, business,
     * destination). `null` when unknown / inactive — never throws.
     *
     * API: {@link ReferralLinkResolveAPI}
     */
    resolve: createAPIMethod<ReferralLinkResolveAPI>(coreAPI, 'referral.linkResolve'),

    /**
     * Record a `view` / `click` hit on a referral link. Deduped
     * server-side per visitor + type + target inside
     * `CONFIG.referral.dedupeWindowMinutes`.
     *
     * API: {@link ReferralTrackingCreateAPI}
     */
    track: createAPIMethod<ReferralTrackingCreateAPI>(coreAPI, 'referral.trackingCreate'),
  };
}

/**
 * Common route APIs (network pricing). v2 Routes are directional
 * (`seller`/`buyer`) and can be `isOwnRoute` (open) or directed. The
 * resolver / hooks layer treats Routes as the single source of truth for
 * lane-based pricing, so every role-specific SDK exposes the same CRUD.
 */
export function createCommonRouteMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get route by ID.
     *
     * API: {@link RouteGetOneAPI}
     */
    getById: createAPIMethod<RouteGetOneAPI>(coreAPI, 'route.getOne'),

    /**
     * List Routes — filter by `sellerId`, `buyerId`, `agreementId`,
     * `isOwnRoute`, or `vehicle`. The controller defaults the scope to the
     * caller's organization unless a super admin overrides.
     *
     * API: {@link RouteGetAllAPI}
     */
    getAll: createAPIMethod<RouteGetAllAPI>(coreAPI, 'route.getAll'),

    /**
     * Create a Route. Server validates seller/buyer presence, vehicle
     * overlap, and agreement-parties consistency.
     *
     * API: {@link RouteCreateAPI}
     */
    create: createAPIMethod<RouteCreateAPI>(coreAPI, 'route.createOne'),

    /**
     * Update a Route. Partial body; same validation as create.
     *
     * API: {@link RouteUpdateAPI}
     */
    update: createAPIMethod<RouteUpdateAPI>(coreAPI, 'route.updateOne'),

    /**
     * Delete a Route. The caller must be the seller / buyer or a super
     * admin.
     *
     * API: {@link RouteDeleteAPI}
     */
    delete: createAPIMethod<RouteDeleteAPI>(coreAPI, 'route.deleteOne'),
  };
}

/**
 * Common agreement APIs (commercial contracts). v2 Agreements are pure
 * metadata wrappers — pricing lives on Routes linked via `agreementId`.
 */
export function createCommonAgreementMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get an Agreement by `_id`.
     *
     * API: {@link AgreementGetOneAPI}
     */
    getOne: createAPIMethod<AgreementGetOneAPI>(coreAPI, 'agreement.getAgreement'),

    /**
     * List Agreements (paginated). Filter by `orgId`, `status`, `type`,
     * or — new in relations-v2 — `partyId` (matches the denormalized
     * `tenants[]` array, so any party id on either side of `parties[]`
     * resolves in a single index seek).
     *
     * API: {@link AgreementGetManyAPI}
     */
    getMany: createAPIMethod<AgreementGetManyAPI>(coreAPI, 'agreement.getManyAgreement'),

    /**
     * Create an Agreement. Bilateral by construction — the caller
     * supplies `orgId` (org-side party) plus `counterparty`
     * (`{_id, target, roles[], name?}`); the service materializes
     * both `Party` snapshots via `PartyResolverService.makeParty` and
     * stamps the matching `tenants[]` + `connectionId` (via
     * `ConnectionService.findOrCreate`). Routes are added separately
     * via `route.create`.
     *
     * API: {@link AgreementCreateAPI}
     */
    create: createAPIMethod<AgreementCreateAPI>(coreAPI, 'agreement.createAgreement'),

    /**
     * Update an Agreement. Terminal status transitions cascade-archive
     * linked Routes.
     *
     * API: {@link AgreementUpdateAPI}
     */
    update: createAPIMethod<AgreementUpdateAPI>(coreAPI, 'agreement.updateAgreement'),
  };
}

/**
 * Common embedded-financing methods. Same shape across every SDK
 * (carrier, customer, admin) because the underlying controller scopes
 * by JWT + `tenants[]`. The two `lender.*` methods are PUBLIC by
 * controller convention — the SDK call still runs through the normal
 * HTTP client, but the backend skips JWT validation.
 *
 * See `apps/backend/src/app/financing/` + `libs/shared/api/src/api/financing.api.ts`.
 */
export function createCommonFinancingMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Legacy alias for `lender.getActive` — kept so the existing
     * `useFinancingPartnersListApi` hook still compiles. New code
     * should call `lenders.getActive` instead.
     *
     * @deprecated
     * API: {@link FinancingGetPartnersAPI}
     */
    getPartners: createAPIMethod<FinancingGetPartnersAPI>(
      coreAPI,
      'financing.getPartners',
    ),
    /**
     * Upload external invoice document (PDF / image). Returns an
     * `ImageWithUpload` to stamp onto `invoice.document` of the
     * subsequent application create call.
     *
     * API: {@link FinancingUploadInvoiceDocumentAPI}
     */
    uploadInvoiceDocument: createAPIMethod<FinancingUploadInvoiceDocumentAPI>(
      coreAPI,
      'financing.uploadInvoiceDocument',
    ),
    /**
     * Create a new FinancingApplication. Server snapshots
     * `Org.financingProfile` into the application and emails the
     * invited lenders (via {@link FinancingNotificationService}).
     *
     * API: {@link FinancingCreateApplicationAPI}
     */
    createApplication: createAPIMethod<FinancingCreateApplicationAPI>(
      coreAPI,
      'financing.createApplication',
    ),
    /**
     * Carrier's "my applications" list. Scoped via `tenants[]`.
     *
     * API: {@link FinancingGetApplicationsAPI}
     */
    getApplications: createAPIMethod<FinancingGetApplicationsAPI>(
      coreAPI,
      'financing.getApplications',
    ),
    /**
     * Carrier accepts one of the `offered` rows. Flips that row's
     * `decision` to `'accepted'` and bumps status to `'accepted'`.
     *
     * API: {@link FinancingAcceptOfferAPI}
     */
    acceptOffer: createAPIMethod<FinancingAcceptOfferAPI>(
      coreAPI,
      'financing.acceptOffer',
    ),
    /**
     * Carrier reads opted-in contacts for the lender on an offer
     * row (`receiveNewApplicationEvent === true`).
     *
     * API: {@link FinancingGetOfferLenderContactsAPI}
     */
    getOfferLenderContacts:
      createAPIMethod<FinancingGetOfferLenderContactsAPI>(
        coreAPI,
        'financing.getOfferLenderContacts',
      ),
    /**
     * Carrier: upload proof of repayment to the lender. Server
     * stores the receipt image via the `image` module under the
     * `financing_repayment_receipts` folder and transitions the
     * application to `awaiting_repayment_approval`.
     *
     * API: {@link FinancingSubmitRepaymentReceiptAPI}
     */
    submitRepaymentReceipt:
      createAPIMethod<FinancingSubmitRepaymentReceiptAPI>(
        coreAPI,
        'financing.submitRepaymentReceipt',
      ),
    /**
     * Admin: approve a carrier-submitted repayment receipt. Flips
     * `awaiting_repayment_approval → repaid` and stamps
     * `outcome.repaidAt`.
     *
     * API: {@link FinancingApproveRepaymentAPI}
     */
    approveRepayment: createAPIMethod<FinancingApproveRepaymentAPI>(
      coreAPI,
      'financing.approveRepayment',
    ),
    /**
     * Admin: reject a carrier-submitted repayment receipt. Reverts
     * `awaiting_repayment_approval → funded`, clears the receipt,
     * and records the rejection in `outcome.notes` so the carrier
     * can re-submit.
     *
     * API: {@link FinancingRejectRepaymentAPI}
     */
    rejectRepayment: createAPIMethod<FinancingRejectRepaymentAPI>(
      coreAPI,
      'financing.rejectRepayment',
    ),
    /**
     * Super-admin: replace the tags[] array on an application.
     * Today admin-curated only; a future sweep cron auto-applies
     * `late` / `overdue` based on `FINANCING_CONFIG.repayment`.
     *
     * API: {@link FinancingUpdateTagsAPI}
     */
    updateTags: createAPIMethod<FinancingUpdateTagsAPI>(
      coreAPI,
      'financing.updateTags',
    ),
    /**
     * Public partner-view of a single application. Looked up by
     * `lenderId` in the URL (no token). Contact info is masked
     * until the partner expresses interest. See
     * `docs/financing/partner-access.md`.
     *
     * API: {@link FinancingPartnerGetAPI}
     */
    partnerGet: createAPIMethod<FinancingPartnerGetAPI>(
      coreAPI,
      'financing.partnerGet',
    ),
    /**
     * Partner clicks "I'm interested" — stamps the offer row and
     * unlocks the contact-reveal on subsequent reads.
     *
     * API: {@link FinancingPartnerExpressInterestAPI}
     */
    partnerExpressInterest:
      createAPIMethod<FinancingPartnerExpressInterestAPI>(
        coreAPI,
        'financing.partnerExpressInterest',
      ),
    /**
     * Partner submits or updates an offer from the partner-view.
     *
     * API: {@link FinancingPartnerSubmitOfferAPI}
     */
    partnerSubmitOffer: createAPIMethod<FinancingPartnerSubmitOfferAPI>(
      coreAPI,
      'financing.partnerSubmitOffer',
    ),
    /**
     * Public single-shot lender-view payload. Returns
     * `{ application, org, client?, agreement?, lender, contactRevealed }`
     * in one call. Persisted via TanStack Query so the page renders
     * the lender display instantly on refresh.
     *
     * API: {@link FinancingLenderViewAPI}
     */
    lenderView: createAPIMethod<FinancingLenderViewAPI>(
      coreAPI,
      'financing.lenderView',
    ),
    /**
     * Lender clicks "I'm interested" on the public page. Returns the
     * same lender-view shape (now with `contactRevealed: true`).
     *
     * API: {@link FinancingLenderExpressInterestAPI}
     */
    lenderExpressInterest: createAPIMethod<FinancingLenderExpressInterestAPI>(
      coreAPI,
      'financing.lenderExpressInterest',
    ),
    /**
     * Lender submits or updates an offer from the public page.
     *
     * API: {@link FinancingLenderSubmitOfferAPI}
     */
    lenderSubmitOffer: createAPIMethod<FinancingLenderSubmitOfferAPI>(
      coreAPI,
      'financing.lenderSubmitOffer',
    ),
    /**
     * Lender stamps the operational funding event from the public
     * page once the carrier has accepted their offer. Mirrors the
     * admin `markOutcome({ kind: 'funded' })` write path.
     *
     * API: {@link FinancingLenderMarkFundedAPI}
     */
    lenderMarkFunded: createAPIMethod<FinancingLenderMarkFundedAPI>(
      coreAPI,
      'financing.lenderMarkFunded',
    ),
    /**
     * Lender stamps the operational repayment event from the public
     * page once the carrier has settled the advance + fee. Mirrors
     * the admin `markOutcome({ kind: 'repaid' })` write path and
     * closes the lifecycle (application transitions to
     * `'repaid'`).
     *
     * API: {@link FinancingLenderMarkRepaidAPI}
     */
    lenderMarkRepaid: createAPIMethod<FinancingLenderMarkRepaidAPI>(
      coreAPI,
      'financing.lenderMarkRepaid',
    ),
    /**
     * Public lender-view write: lender rejects a carrier-submitted
     * repayment receipt. Status reverts to `funded`, the receipt is
     * cleared, and `notes` is surfaced to the carrier so they can
     * fix and re-upload.
     *
     * API: {@link FinancingLenderRejectRepaymentAPI}
     */
    lenderRejectRepayment: createAPIMethod<FinancingLenderRejectRepaymentAPI>(
      coreAPI,
      'financing.lenderRejectRepayment',
    ),
    /** Admin: mark funded/repaid/defaulted outcome on an application. */
    markOutcome: createAPIMethod<FinancingMarkOutcomeAPI>(
      coreAPI,
      'financing.markOutcome',
    ),
    /**
     * Admin: flip a `pending_review` application to `submitted` and
     * fan the lender invites. Skips lender resolution and emails when
     * `FINANCING_CONFIG.autoSendApplications=true` (already done at
     * create time in that mode).
     *
     * API: {@link FinancingSendToLendersAPI}
     */
    sendToLenders: createAPIMethod<FinancingSendToLendersAPI>(
      coreAPI,
      'financing.sendToLenders',
    ),
    /**
     * Admin: cancel a `pending_review` or `submitted` application.
     * Records a `cancelled_by_admin` event.
     *
     * API: {@link FinancingCancelAPI}
     */
    adminCancel: createAPIMethod<FinancingCancelAPI>(
      coreAPI,
      'financing.adminCancel',
    ),
    /**
     * Admin: patch editable invoice fields on a financing
     * application (amount / due date / invoice number / customer
     * name / document). Source-discriminator + linked id are
     * immutable post-submit.
     *
     * API: {@link FinancingAdminUpdateInvoiceAPI}
     */
    adminUpdateInvoice: createAPIMethod<FinancingAdminUpdateInvoiceAPI>(
      coreAPI,
      'financing.adminUpdateInvoice',
    ),
    /**
     * Admin: append or edit an offer on a financing application
     * out-of-band (e.g. lender emailed Hero with terms instead of
     * using the partner-view UI). Mode is discriminated by whether
     * the body carries an `offerId`.
     *
     * API: {@link FinancingAdminUpsertOfferAPI}
     */
    adminUpsertOffer: createAPIMethod<FinancingAdminUpsertOfferAPI>(
      coreAPI,
      'financing.adminUpsertOffer',
    ),
    /** Admin: list all applications with filters. */
    adminGetAll: createAPIMethod<FinancingAdminGetAllAPI>(
      coreAPI,
      'financing.adminGetAll',
    ),
    /** Admin: read one application by id (for the drawer). */
    adminGetOne: createAPIMethod<FinancingAdminGetOneAPI>(
      coreAPI,
      'financing.adminGetOne',
    ),
  };
}

/**
 * Common connection APIs (relations-v2). Lifecycle endpoints for the
 * `Connection` collection. Writes happen implicitly via commercial
 * actions (Offer accept / Order create / Agreement create / Route
 * create); this surface is only for reads + accept / block.
 *
 * See `docs/relations-v2/02-data-model.md` section 6.
 */
export function createCommonConnectionMethods(coreAPI: CoreAPI) {
  return {
    /** "My Network" — list connections for the current org. */
    getAll: createAPIMethod<ConnectionGetAllAPI>(coreAPI, 'connection.getAll'),

    /** Read one connection by id. */
    getById: createAPIMethod<ConnectionGetOneAPI>(coreAPI, 'connection.getOne'),

    /** Flip `pending` → `accepted`. Idempotent. */
    accept: createAPIMethod<ConnectionAcceptAPI>(coreAPI, 'connection.accept'),

    /** Block — fails commercial actions while in this state. */
    block: createAPIMethod<ConnectionBlockAPI>(coreAPI, 'connection.block'),
  };
}

/**
 * Common profile APIs (relations-v2). Read-only for any signed-in org
 * (used by the Brand Directory drawer in the Create-Client flow);
 * writes are super-admin only and live on the admin SDK.
 */
export function createCommonProfileMethods(coreAPI: CoreAPI) {
  return {
    /** Brand directory list. */
    getAll: createAPIMethod<ProfileGetAllAPI>(coreAPI, 'profile.getAll'),

    /** Brand directory entry by id. */
    getById: createAPIMethod<ProfileGetOneAPI>(coreAPI, 'profile.getOne'),

    /**
     * Tenant-facing "suggest a new brand" write. Server pins
     * `source: 'suggested'` and admins triage in the Brand
     * Directory list. Used by {@link EntityDirectoryPicker}.
     *
     * API: {@link ProfileSuggestAPI}
     */
    suggest: createAPIMethod<ProfileSuggestAPI>(coreAPI, 'profile.suggest'),
  };
}

/**
 * Admin profile APIs — super-admin only. Lives next to
 * {@link createCommonProfileMethods} so we keep one source of truth
 * for the SDK shape.
 */
export function createAdminProfileMethods(coreAPI: CoreAPI) {
  return {
    create: createAPIMethod<ProfileCreateAdminAPI>(coreAPI, 'profile.createOne'),
    update: createAPIMethod<ProfileUpdateAdminAPI>(coreAPI, 'profile.updateOne'),
    delete: createAPIMethod<ProfileDeleteAdminAPI>(coreAPI, 'profile.deleteOne'),
    bulkImport: createAPIMethod<ProfileBulkImportAdminAPI>(
      coreAPI,
      'profile.bulkImport',
    ),
  };
}

/**
 * Common Org APIs (post relations-v2). The unified `Org` collection is
 * the single source of truth for every organizational entity that used
 * to live under `Business` or `Carrier`. List + get-one are exposed
 * here; create/update flows still go through the unified signup
 * (`POST /auth/sign-up`) and the role-agnostic
 * `organization.update-profile` endpoint until Phase 6 lands the
 * dedicated `org.create` / `org.update` / `org.delete` controllers.
 */
export function createCommonOrgMethods(coreAPI: CoreAPI) {
  return {
    getAll: createAPIMethod<OrgGetAllAPI>(coreAPI, 'org.getAll'),
    getById: createAPIMethod<OrgGetOneAPI>(coreAPI, 'org.getOne'),
  };
}

/**
 * Common saved-location APIs that should be included in all SDKs' savedLocations namespace.
 * SavedLocation CRM is shared between customer, carrier, and admin roles (each filters by auth context).
 */
export function createCommonSavedLocationMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Get all saved locations
     *
     * API: {@link SavedLocationGetAllAPI}
     */
    getAll: createAPIMethod<SavedLocationGetAllAPI>(coreAPI, 'savedLocations.getAll'),

    /**
     * Get saved location by ID
     *
     * API: {@link SavedLocationGetOneAPI}
     */
    getById: createAPIMethod<SavedLocationGetOneAPI>(coreAPI, 'savedLocations.getOne'),

    /**
     * Search saved locations
     *
     * API: {@link SavedLocationSearchAPI}
     */
    search: createAPIMethod<SavedLocationSearchAPI>(coreAPI, 'savedLocations.search'),

    /**
     * Create saved location
     *
     * API: {@link SavedLocationCreateAPI}
     */
    create: createAPIMethod<SavedLocationCreateAPI>(coreAPI, 'savedLocations.createOne'),

    /**
     * Update saved location
     *
     * API: {@link SavedLocationUpdateAPI}
     */
    update: createAPIMethod<SavedLocationUpdateAPI>(coreAPI, 'savedLocations.updateOne'),

    /**
     * Delete saved location
     *
     * API: {@link SavedLocationDeleteAPI}
     */
    delete: createAPIMethod<SavedLocationDeleteAPI>(coreAPI, 'savedLocations.deleteOne'),

    /**
     * Resolve a Google Maps share URL (long or short `maps.app.goo.gl`
     * link) into `{ lat, lng, name?, address? }` for prefilling the
     * location picker. Used when the user can't pin the exact spot on
     * the map and instead pastes a share link as a reference.
     *
     * API: {@link SavedLocationResolveGoogleUrlAPI}
     */
    resolveGoogleUrl: createAPIMethod<SavedLocationResolveGoogleUrlAPI>(
      coreAPI,
      'savedLocations.resolveGoogleUrl',
    ),
  };
}

/**
 * Common SALES methods — Hero-as-seller (subscriptions, billing, features).
 * Strictly separate from `createCommonPaymentMethods` which is freight payments.
 *
 * Every dashboard ships with this namespace because every signed-in org
 * (carrier / customer / broker / driver-side) needs to see + manage its
 * subscription.
 */
export function createCommonSalesMethods(coreAPI: CoreAPI) {
  return {
    subscription: {
      /** Current org's subscription, or null if on the implicit free tier. */
      getMine: createAPIMethod<SubscriptionGetMineAPI>(coreAPI, 'subscription.getMine'),
      /**
       * Live consumption for the current period — the same meters the
       * renewal invoice prices from. Read this instead of counting
       * orders client-side.
       */
      getUsage: createAPIMethod<SubscriptionGetUsageAPI>(coreAPI, 'subscription.getUsage'),
      /** Admin lookup of an owner's subscription (`ownerType` = org | user). */
      adminGet: createAPIMethod<SubscriptionGetAdminAPI>(coreAPI, 'subscription.adminGet'),
      /** Admin: paginated directory of every subscription across the platform. */
      adminGetAll: createAPIMethod<SubscriptionAdminGetAllAPI>(
        coreAPI,
        'subscription.adminGetAll',
      ),
      /** Start a new subscription (returns checkoutUrl for first payment). */
      subscribe: createAPIMethod<SubscriptionSubscribeAPI>(coreAPI, 'subscription.subscribe'),
      /**
       * Single user-initiated payment entry. Handles trial conversion
       * (returns `checkoutUrl`) and mid-period upgrades for active orgs
       * with a saved card (silent charge — `charged: true`, no URL).
       * Use this instead of `changePlan` / `changeInterval` / `changeSeats`
       * in the dashboard.
       */
      pay: createAPIMethod<SubscriptionPayAPI>(coreAPI, 'subscription.pay'),
      /** Admin-only: change plan field (no charge). Use `pay` for the user flow. */
      changePlan: createAPIMethod<SubscriptionChangePlanAPI>(coreAPI, 'subscription.changePlan'),
      /** Change interval (effective next period). */
      changeInterval: createAPIMethod<SubscriptionChangeIntervalAPI>(
        coreAPI,
        'subscription.changeInterval',
      ),
      /** Change committed vehicle quantity. */
      changeSeats: createAPIMethod<SubscriptionChangeSeatsAPI>(
        coreAPI,
        'subscription.changeSeats',
      ),
      /** Add or update an addon. One-time addons charge immediately. */
      addAddon: createAPIMethod<SubscriptionAddAddonAPI>(coreAPI, 'subscription.addAddon'),
      /** Remove a recurring addon at period end. */
      removeAddon: createAPIMethod<SubscriptionRemoveAddonAPI>(
        coreAPI,
        'subscription.removeAddon',
      ),
      /** Cancel at period end (default) or immediately. */
      cancel: createAPIMethod<SubscriptionCancelAPI>(coreAPI, 'subscription.cancel'),
      /** Admin: set per-org pricing override. */
      adminOverride: createAPIMethod<SubscriptionAdminOverrideAPI>(
        coreAPI,
        'subscription.adminOverride',
      ),
      /** Admin: force a renewal cycle (QA / recovery). */
      adminForceRenew: createAPIMethod<SubscriptionAdminForceRenewAPI>(
        coreAPI,
        'subscription.adminForceRenew',
      ),
      /** Admin: gift an owner (org or freelancer user) credits — `free` or `repayable`, optional expiry. */
      adminGrantCredits: createAPIMethod<SubscriptionAdminGrantCreditsAPI>(
        coreAPI,
        'subscription.adminGrantCredits',
      ),
      /** Admin: revoke an owner's outstanding grants (reclaims unspent free credits). */
      adminRevokeCredits: createAPIMethod<SubscriptionAdminRevokeCreditsAPI>(
        coreAPI,
        'subscription.adminRevokeCredits',
      ),
      /** Super-admin: the global "make everyone zero". Requires `confirm: true`. */
      adminResetFreeCredits: createAPIMethod<SubscriptionAdminResetFreeCreditsAPI>(
        coreAPI,
        'subscription.adminResetFreeCredits',
      ),
      paymentMethodCheckout: createAPIMethod<SubscriptionPaymentMethodCheckoutAPI>(
        coreAPI,
        'subscription.paymentMethodCheckout',
      ),
      paymentMethodDelete: createAPIMethod<SubscriptionPaymentMethodDeleteAPI>(
        coreAPI,
        'subscription.paymentMethodDelete',
      ),
    },
    billing: {
      /** Single Billing doc by id. */
      getOne: createAPIMethod<BillingGetOneAPI>(coreAPI, 'billing.getOne'),
      /** Paginated list scoped to the caller's org. */
      getMany: createAPIMethod<BillingGetManyAPI>(coreAPI, 'billing.getMany'),
      /** Admin: paginated list across all orgs. */
      adminGetAll: createAPIMethod<BillingAdminGetAllAPI>(coreAPI, 'billing.adminGetAll'),
      /** Admin: cancel a draft / pending Billing. */
      adminCancel: createAPIMethod<BillingAdminCancelAPI>(coreAPI, 'billing.adminCancel'),
    },
    billingTransaction: {
      /** Single BTx by id. */
      getOne: createAPIMethod<BillingTransactionGetOneAPI>(
        coreAPI,
        'billingTransaction.getOne',
      ),
      /** Paginated list scoped to the caller's org / specific Billing. */
      getMany: createAPIMethod<BillingTransactionGetManyAPI>(
        coreAPI,
        'billingTransaction.getMany',
      ),
      /** Admin: issue a refund against the last successful charge. */
      adminRefund: createAPIMethod<BillingTransactionAdminRefundAPI>(
        coreAPI,
        'billingTransaction.adminRefund',
      ),
      /**
       * Resolve a Moyasar payment id back to the BTx + parent Billing.
       * Powers the payment-confirmation return page poll.
       */
      byGatewayPaymentId: createAPIMethod<BillingTransactionByGatewayPaymentIdAPI>(
        coreAPI,
        'billingTransaction.byGatewayPaymentId',
      ),
    },
    features: {
      /** Resolved features for the caller's org. */
      getMine: createAPIMethod<FeaturesGetMineAPI>(coreAPI, 'features.getMine'),
      /** Admin lookup by orgId. */
      adminGet: createAPIMethod<FeaturesGetAdminAPI>(coreAPI, 'features.adminGet'),
    },
  };
}

/**
 * Common lender directory methods. The lender directory is the
 * single source of truth for financing partners (no static config,
 * no env seed) — see `apps/backend/src/app/lender/README.md`.
 *
 * Only the public `getActive` is exposed here so every consumer SDK
 * (carrier, customer, admin) can render the partners strip; admin
 * CRUD lives on `AdminAPI.lenders` via the dedicated admin SDK file.
 */
export function createCommonLenderMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Active lender directory — display-safe fields only. Backs the
     * carrier dashboard's partners strip and the apply form's lender
     * preview.
     *
     * API: {@link LenderGetActiveAPI}
     */
    getActive: createAPIMethod<LenderGetActiveAPI>(coreAPI, 'lender.getActive'),
  };
}

/**
 * Hero AI agent methods.
 *
 * `sendMessage` is special — it does not return a single Promise but
 * an async iterator of {@link AgentSSEEvent}. The implementation
 * uses Fetch (not Axios) because EventSource / streaming responses
 * aren't natively supported by Axios, and the standardised
 * `ReadableStream` API is the right primitive for SSE.
 *
 * `confirmMessage` is a normal JSON request and follows the rest of
 * the SDK conventions via `createAPIMethod`.
 *
 * See [`docs/ai/architecture.md`](../../../../../../docs/ai/architecture.md)
 * "SSE event catalog".
 */
export function createCommonAgentMethods(coreAPI: CoreAPI) {
  return {
    sendMessage: createAgentSendMessageStream(coreAPI),
    confirmMessage: createAPIMethod<AgentConfirmMessageAPI>(
      coreAPI,
      'agent.confirmMessage',
    ),
    /**
     * The caller's own conversation threads, newest activity first.
     *
     * API: {@link AgentListConversationsAPI}
     */
    listConversations: createAPIMethod<AgentListConversationsAPI>(
      coreAPI,
      'agent.listConversations',
    ),
    /**
     * Full transcript of one conversation — the chat panel replays it
     * after a reload so the thread (and any still-open confirmation
     * card) survives a refresh.
     *
     * API: {@link AgentGetTranscriptAPI}
     */
    getTranscript: createAPIMethod<AgentGetTranscriptAPI>(
      coreAPI,
      'agent.getTranscript',
    ),
    /**
     * A playable URL for an attachment held in private storage.
     *
     * Voice clips live in a private bucket, so `attachment.url` is an
     * object address rather than something an `<audio>` element can
     * load. Call this to trade it for a short-lived signed URL — and
     * treat the result as perishable: it expires, so resolve it when
     * the player mounts rather than caching it with the transcript.
     *
     * API: {@link AgentMediaUrlAPI}
     */
    getMediaUrl: createAPIMethod<AgentMediaUrlAPI>(coreAPI, 'agent.mediaUrl'),
  };
}

/**
 * Internal: streamed `POST /agent/messages` helper. Uses Fetch +
 * `ReadableStream` so we can `for await` over SSE chunks. Auth +
 * language headers are pulled from the same token adapter the
 * Axios client uses, so the streaming path looks identical to the
 * rest of the SDK from the caller's POV.
 */
function createAgentSendMessageStream(coreAPI: CoreAPI) {
  const API_NAME = 'agent.sendMessage' as const;

  // Reach through the `protected` visibility on `CoreAPI` — TypeScript's
  // `protected` is compile-time only, so a single `unknown` cast gives us
  // the SDK config / token adapter at runtime. The Axios-based methods on
  // `CoreAPI` call into these the same way through `this.config` /
  // `this.httpClient`; we just inline that here because SSE needs Fetch.
  const internals = coreAPI as unknown as {
    config: {
      baseURL: string;
      enableLogs?: boolean;
      // Both required on `SDKConfig` — mirrored here so the SSE
      // path can attach the same `x-hero-app` + `x-hero-platform`
      // pair every axios request uses.
      app: string;
      platform: string;
      infoAdapter?: () =>
        | { access_token?: string | null; language?: string | null }
        | Promise<{ access_token?: string | null; language?: string | null }>;
    };
  };

  return async function* (
    body: AgentSendMessageAPI['req'],
    options?: { signal?: AbortSignal },
  ): AsyncGenerator<AgentSSEEvent, void, void> {
    const baseURL = internals.config.baseURL;
    const route = getAPIEndpoint(API_NAME);
    const url = route.endpoint(baseURL);
    const enableLogs = internals.config.enableLogs ?? false;
    const logCtx = { apiName: API_NAME, method: 'POST', url, req: body };

    if (enableLogs) {
      try {
        logAgentStreamStart(logCtx);
      } catch {
        /* logging must never break the request pipeline */
      }
    }

    // Call the same `infoAdapter` the Axios interceptor uses (see
    // `HttpClient.getAdapterData`), but inline it here so we don't have
    // to reach into a private method and lose `this` binding.
    const adapterResult = internals.config.infoAdapter?.();
    const adapterData = adapterResult
      ? adapterResult instanceof Promise
        ? await adapterResult
        : adapterResult
      : {};

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      // Same paired-header convention every other request uses —
      // see `CONFIG.headers` for the canonical registry.
      [CONFIG.headers.app]: internals.config.app,
      [CONFIG.headers.platform]: internals.config.platform,
    };
    if (adapterData.access_token) {
      headers[CONFIG.headers.authorization] = `Bearer ${adapterData.access_token}`;
    }
    if (adapterData.language) {
      headers[CONFIG.headers.language] = adapterData.language;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: options?.signal,
    });

    if (!response.ok || !response.body) {
      let errorData: { message?: string; error?: { errorId?: string } } | undefined;
      try {
        errorData = (await response.json()) as typeof errorData;
      } catch {
        /* non-JSON error body — fall through */
      }
      const message =
        errorData?.message ??
        `Agent stream failed: ${response.status} ${response.statusText}`;

      if (enableLogs) {
        try {
          logAgentStreamHttpError({
            ...logCtx,
            status: response.status,
            message,
            errorData,
          });
        } catch {
          /* logging must never break the request pipeline */
        }
      }

      throw new SDKError(
        message,
        response.status,
        errorData?.error?.errorId,
        errorData,
      );
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let blockEnd = buffer.indexOf('\n\n');
        while (blockEnd !== -1) {
          const block = buffer.slice(0, blockEnd);
          buffer = buffer.slice(blockEnd + 2);

          // Comment-only blocks (heartbeat) start with ":"
          const dataLines = block
            .split('\n')
            .filter(line => line.startsWith('data: '))
            .map(line => line.slice(6));
          if (dataLines.length > 0) {
            const dataPayload = dataLines.join('\n');
            try {
              const event = JSON.parse(dataPayload) as AgentSSEEvent;

              if (enableLogs && event.type === 'error') {
                try {
                  logAgentStreamEventError({
                    ...logCtx,
                    code: event.code,
                    message: event.message,
                    referenceId: event.referenceId,
                    runId: event.runId,
                  });
                } catch {
                  /* logging must never break the request pipeline */
                }
              }

              if (enableLogs && event.type === 'run_finished') {
                try {
                  logAgentStreamFinished({
                    ...logCtx,
                    status: event.status,
                    runId: event.runId,
                  });
                } catch {
                  /* logging must never break the request pipeline */
                }
              }

              yield event;
            } catch {
              // Malformed event — skip; the next event recovers.
            }
          }
          blockEnd = buffer.indexOf('\n\n');
        }
      }
    } finally {
      reader.releaseLock();
    }
  };
}

/**
 * Transactional messaging (WhatsApp waybill today; email later).
 *
 * API: {@link MessageSendAPI}
 */
export function createCommonMessageMethods(coreAPI: CoreAPI) {
  return {
    /**
     * Send an order message (e.g. WhatsApp waybill) to one or more phones.
     *
     * API: {@link MessageSendAPI}
     */
    send: createAPIMethod<MessageSendAPI>(coreAPI, 'message.send'),
  };
}
